import { Plus } from 'lucide-react';
import { PROJECTS } from '../data/projects';
import { trackEvent } from '../utils/analytics';

function Column({ label, text }) {
  return (
    <div>
      <p className="label mb-3">{label}</p>
      <p className="text-sm leading-relaxed text-muted">{text}</p>
    </div>
  );
}

function CaseStudyItem({ project, index, defaultOpen }) {
  const s = project.caseStudy;
  return (
    <details
      className="reveal group border-t border-line last:border-b"
      style={{ '--d': `${index * 60}ms` }}
      open={defaultOpen}
      onToggle={(e) => {
        if (e.currentTarget.open) trackEvent('case_study_open', { project: project.name });
      }}
    >
      <summary className="flex items-center gap-5 py-6 lg:py-7 select-none">
        <span className="font-mono text-xs text-faint w-8 shrink-0">{project.num}</span>
        <span className="flex-1 min-w-0">
          <span className="display block text-xl sm:text-2xl group-hover:text-accent transition-colors">
            {project.name}
          </span>
          <span className="block mt-1 text-sm text-faint truncate">{project.subtitle}</span>
        </span>
        <span className="hidden md:flex flex-wrap justify-end gap-1.5 max-w-xs">
          {project.metrics?.slice(0, 2).map((m) => (
            <span key={m} className="chip">{m}</span>
          ))}
        </span>
        <span className="chev h-10 w-10 shrink-0 rounded-full border border-line flex items-center justify-center text-muted group-hover:border-ink group-hover:text-ink transition-colors">
          <Plus size={16} aria-hidden="true" />
        </span>
      </summary>

      <div className="details-body pb-10 lg:pl-[3.25rem]">
        <div className="grid md:grid-cols-3 gap-8">
          <Column label="Problem" text={s.problem} />
          <Column label="Approach" text={s.approach} />
          <Column label="Outcome" text={s.outcome} />
        </div>

        {(s.stack || project.architecture) && (
          <div className="mt-8 card p-6">
            <p className="label mb-4">Architecture</p>
            {s.stack ? (
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {s.stack.map((row) => (
                  <div key={row.layer} className="flex gap-4">
                    <dt className="font-mono text-[11px] text-accent w-20 shrink-0 pt-0.5">{row.layer}</dt>
                    <dd className="text-sm text-muted leading-relaxed">{row.detail}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-mono text-xs text-muted">{project.architecture}</p>
            )}
          </div>
        )}

        {s.learned && (
          <blockquote className="mt-8 max-w-3xl">
            <p className="font-serif italic text-xl sm:text-2xl leading-snug text-ink">
              &ldquo;{s.learned}&rdquo;
            </p>
            <cite className="mt-3 block label not-italic">What I learned</cite>
          </blockquote>
        )}
      </div>
    </details>
  );
}

export default function CaseStudies() {
  const items = PROJECTS.filter((p) => p.caseStudy);
  return (
    <div className="mt-24 lg:mt-32" aria-labelledby="case-studies-title">
      <div className="reveal mb-8 flex items-end justify-between gap-6">
        <div>
          <p className="label mb-4">Case studies</p>
          <h3 id="case-studies-title" className="display text-3xl sm:text-4xl">
            The <em>longer</em> version.
          </h3>
        </div>
        <p className="hidden md:block text-sm text-faint max-w-xs text-right">
          What the problem was, what I tried, and how it went. Open a row.
        </p>
      </div>
      <div>
        {items.map((p, i) => (
          <CaseStudyItem key={p.num} project={p} index={i} defaultOpen={i === 0} />
        ))}
      </div>
    </div>
  );
}
